import { pdf } from "@react-pdf/renderer";
import { PDFDocument } from "pdf-lib";
import { ReactElement } from "react";

export type PdfSource = { label: string; bytes: Uint8Array | null };
export type MergeResult = { pdfBytes: Uint8Array; skippedDocs: string[] };

/**
 * Convert a @react-pdf/renderer React element to raw PDF bytes.
 */
export async function reactPdfToBytes(document: ReactElement): Promise<Uint8Array> {
  const blob = await pdf(document).toBlob();
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * Merge multiple PDF sources into a single PDF document.
 * Sources with null bytes or that fail to load are skipped and reported.
 */
export async function mergePdfs(sources: PdfSource[]): Promise<MergeResult> {
  const merged = await PDFDocument.create();
  const skippedDocs: string[] = [];

  for (const source of sources) {
    if (!source.bytes || source.bytes.length === 0) {
      skippedDocs.push(source.label);
      continue;
    }
    try {
      const srcDoc = await PDFDocument.load(source.bytes, { ignoreEncryption: true });
      const pages = await merged.copyPages(srcDoc, srcDoc.getPageIndices());
      for (const page of pages) {
        merged.addPage(page);
      }
    } catch {
      skippedDocs.push(source.label);
    }
  }

  const pdfBytes = await merged.save();
  return { pdfBytes, skippedDocs };
}

/**
 * Trigger a browser download from raw PDF bytes.
 */
export function downloadPdfBytes(pdfBytes: Uint8Array, filename: string): void {
  const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
